import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import {
  Plus, Loader2, AlertTriangle, Users, Calendar, RefreshCw, X, CheckCircle, ExternalLink,
} from 'lucide-react';
import {
  NewCohortModal,
  CohortDetailModal,
  COHORT_TYPES,
  COHORT_STATUS,
  WEEKDAYS,
} from './CohortsAdmin';

const API = `${process.env.REACT_APP_BACKEND_URL || ''}/api`;
const authHeaders = () => {
  const t = localStorage.getItem('crm_token');
  return t ? { Authorization: `Bearer ${t}` } : {};
};

export default function KundenModuleBoard({ leads, cohorts, onReload, onReloadCohorts }) {
  const [error, setError] = useState('');
  const [showNew, setShowNew] = useState(false);
  const [activeCohortId, setActiveCohortId] = useState(null);
  const [draggedLeadId, setDraggedLeadId] = useState(null);
  const [dragOverCohortId, setDragOverCohortId] = useState(null);
  const [busyCohortId, setBusyCohortId] = useState(null);
  const [mobileTab, setMobileTab] = useState('kunden'); // mobile: 'kunden' | 'module'
  const [activeMember, setActiveMember] = useState(null);   // {lead_id, ready_check_id, company_name}

  // Loading-Guard: cohorts === undefined heißt "Parent lädt noch" → kein Render-Flash
  const cohortsReady = Array.isArray(cohorts);
  const cohortsSafe = cohortsReady ? cohorts : [];

  // Kunden = Leads mit kanban_status='kunde', die NICHT in einer aktiven Kohorte sind.
  // Sebastian-Regel: Kunde in 'planning' oder 'active' Kohorte → aus der Liste raus.
  // Kohorte 'completed' / 'archived' → Kunde wieder verfügbar zum Re-Zuweisen.
  const blockedLeadIds = new Set();
  for (const c of cohortsSafe) {
    if (c.status === 'planning' || c.status === 'active') {
      for (const lid of (c.member_lead_ids || [])) {
        if (lid) blockedLeadIds.add(lid);
      }
    }
  }
  const kunden = (leads || []).filter((l) =>
    l.kanban_status === 'kunde' && !blockedLeadIds.has(l.id)
  );

  const loadCohorts = useCallback(async () => {
    setError('');
    if (onReloadCohorts) await onReloadCohorts();
  }, [onReloadCohorts]);

  // Wenn die Daten noch nicht da sind: ein zentraler Spinner — kein "Flash"-Layout
  if (!cohortsReady) {
    return (
      <div className="flex-1 flex items-center justify-center bg-[#fafafa]">
        <div className="text-center">
          <Loader2 size={32} className="animate-spin mx-auto text-gray-400 mb-2" />
          <p className="text-xs text-gray-400">Module + Kunden werden geladen…</p>
        </div>
      </div>
    );
  }

  // Drag-Source 1: frischer Kunde aus der linken Liste → start-modul-1
  const handleLeadDragStart = (e, leadId) => {
    setDraggedLeadId(leadId);
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('application/x-lead-id', leadId);
    e.dataTransfer.setData('text/plain', leadId);   // Fallback
  };
  // Drag-Source 2: Member aus einer Kohorte → move-member zur Ziel-Kohorte
  const handleMemberDragStart = (e, readyCheckId, sourceCohortId) => {
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('application/x-readycheck-id', readyCheckId);
    e.dataTransfer.setData('application/x-source-cohort-id', sourceCohortId);
    e.stopPropagation();   // Card-Click nicht triggern
  };

  const handleDragEnd = () => { setDraggedLeadId(null); setDragOverCohortId(null); };
  const handleDragOver = (e, cohortId) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    setDragOverCohortId(cohortId);
  };
  const handleDragLeave = (cohortId) => { if (dragOverCohortId === cohortId) setDragOverCohortId(null); };

  const handleDrop = async (e, cohortId) => {
    e.preventDefault();
    e.stopPropagation();   // UX-Fix: verhindert dass nach Drop noch ein Click-Event die Card öffnet
    const leadId = e.dataTransfer.getData('application/x-lead-id') || draggedLeadId;
    const readyCheckId = e.dataTransfer.getData('application/x-readycheck-id');
    const sourceCohortId = e.dataTransfer.getData('application/x-source-cohort-id');
    setDragOverCohortId(null); setDraggedLeadId(null);

    // Move-Member zwischen Kohorten?
    if (readyCheckId && sourceCohortId && sourceCohortId !== cohortId) {
      setBusyCohortId(cohortId);
      try {
        await axios.post(`${API}/admin/cohorts/${cohortId}/move-member`,
          { ready_check_id: readyCheckId },
          { headers: authHeaders() });
        await loadCohorts();
      } catch (err) {
        alert(err.response?.data?.detail || 'Konnte Mitglied nicht verschieben');
      }
      setBusyCohortId(null);
      return;
    }

    // Frischer Kunde aus links?
    if (leadId) {
      setBusyCohortId(cohortId);
      try {
        await axios.post(`${API}/admin/leads/${leadId}/start-modul-1`, {
          cohort_id: cohortId, send_mail: true,
        }, { headers: authHeaders() });
        await loadCohorts();
        if (onReload) onReload();
      } catch (err) {
        alert(err.response?.data?.detail || 'Konnte Kunde nicht zur Kohorte hinzufügen');
      }
      setBusyCohortId(null);
    }
  };

  return (
    <div className="flex-1 flex flex-col min-h-0">
      {/* Top-Header mit Neues-Modul-Button */}
      <div className="bg-white border-b border-gray-200 px-3 md:px-6 py-2 md:py-3 flex items-center justify-between gap-2 sticky top-0 z-10">
        <div className="hidden md:flex gap-4 text-xs text-gray-500">
          <span><span className="text-[#1f1f1f] font-bold">{kunden.length}</span> Kunden</span>
          <span><span className="text-[#1f1f1f] font-bold">{cohortsSafe.length}</span> Module</span>
        </div>
        {/* Mobile Tabs */}
        <div className="md:hidden flex bg-gray-100 rounded-lg p-0.5">
          <button onClick={() => setMobileTab('kunden')}
            className={`px-3 py-1.5 text-xs font-bold uppercase rounded-md ${mobileTab === 'kunden' ? 'bg-[#0a0a0a] text-white' : 'text-gray-500'}`}
            style={{ fontFamily: "'Oswald', sans-serif" }}>
            Kunden ({kunden.length})
          </button>
          <button onClick={() => setMobileTab('module')}
            className={`px-3 py-1.5 text-xs font-bold uppercase rounded-md ${mobileTab === 'module' ? 'bg-[#0a0a0a] text-white' : 'text-gray-500'}`}
            style={{ fontFamily: "'Oswald', sans-serif" }}>
            Module ({cohortsSafe.length})
          </button>
        </div>
        <div className="flex items-center gap-2 flex-shrink-0">
          <button onClick={loadCohorts} className="border border-gray-300 px-2 md:px-3 py-1.5 text-xs uppercase tracking-wider hover:bg-gray-50 inline-flex items-center gap-1"
            style={{ fontFamily: "'Oswald', sans-serif" }}>
            <RefreshCw size={12} /> <span className="hidden md:inline">Neu laden</span>
          </button>
          <button onClick={() => setShowNew(true)}
            className="bg-[#befa0a] text-[#0a0a0a] px-3 md:px-4 py-1.5 text-xs md:text-sm font-bold uppercase tracking-wider rounded hover:bg-lime-300 flex items-center gap-1.5"
            style={{ fontFamily: "'Oswald', sans-serif" }}>
            <Plus size={14} /> <span className="hidden sm:inline">Neues Modul</span><span className="sm:hidden">Neu</span>
          </button>
        </div>
      </div>

      {error && (
        <div className="bg-red-50 border-b border-red-200 text-red-700 p-3 text-sm">
          <AlertTriangle size={14} className="inline mr-2" /> {error}
        </div>
      )}

      {/* Cohort-Detail-Modus: Inline-Layout (links Cohort-Panel, rechts Member-Panel) */}
      {activeCohortId ? (
        <div className="flex-1 flex min-h-0 overflow-hidden">
          {/* LINKS: Cohort-Detail-Panel (direkt an Sidebar angedockt) */}
          <div className="w-full md:w-[520px] flex-shrink-0 flex flex-col min-h-0">
            <CohortDetailModal
              cohortId={activeCohortId}
              inline
              onMemberClick={(m) => setActiveMember({
                lead_id: m.lead_id,
                ready_check_id: m.id,
                company_name: m.company_name,
              })}
              onClose={() => { setActiveCohortId(null); setActiveMember(null); loadCohorts(); if (onReload) onReload(); }} />
          </div>
          {/* RECHTS: Member-Detail-Panel (nur sichtbar wenn activeMember) */}
          <div className="hidden md:flex flex-1 flex-col bg-[#fafafa] overflow-y-auto">
            {activeMember ? (
              <MemberKickoffModal
                leadId={activeMember.lead_id}
                companyName={activeMember.company_name}
                inline
                onClose={() => { setActiveMember(null); loadCohorts(); }} />
            ) : (
              <div className="flex-1 flex items-center justify-center text-center p-8">
                <div className="max-w-sm">
                  <Users size={32} className="mx-auto text-gray-300 mb-3" />
                  <p className="text-sm text-gray-500" style={{ fontFamily: "'Manrope', sans-serif" }}>
                    Klick links auf ein Kohorten-Mitglied, um Kunde anzusehen, Daten zu ändern oder einen 1:1-Termin zu buchen.
                  </p>
                </div>
              </div>
            )}
          </div>
        </div>
      ) : (
      /* Default-Modus: Kundenliste + Cohort-Grid */
      <div className="flex-1 flex min-h-0 overflow-hidden">
        {/* LEFT: Kunden-Liste (Drag-Source) */}
        <aside className={`${mobileTab === 'kunden' ? 'flex' : 'hidden'} md:flex flex-col border-r border-gray-200 bg-white w-full md:w-[300px] flex-shrink-0 overflow-y-auto`}>
          <div className="px-4 py-3 border-b border-gray-100 sticky top-0 bg-white z-[5]">
            <p className="text-[10px] uppercase tracking-wider text-gray-500 font-bold" style={{ fontFamily: "'Oswald', sans-serif" }}>
              Kunden ({kunden.length})
            </p>
            <p className="text-[10px] text-gray-400 mt-0.5">Per Drag-and-Drop in ein Modul ziehen.</p>
          </div>
          {kunden.length === 0 ? (
            <p className="p-4 text-xs text-gray-400 italic">Noch keine Kunden. Aus dem Leads-Tab in "Kunde" schieben.</p>
          ) : (
            <ul className="p-2 space-y-1.5">
              {kunden.map((k) => (
                <li key={k.id}
                  draggable
                  onDragStart={(e) => handleLeadDragStart(e, k.id)}
                  onDragEnd={handleDragEnd}
                  className={`p-2.5 border bg-white cursor-grab active:cursor-grabbing hover:border-[#658a07] transition-colors ${draggedLeadId === k.id ? 'opacity-40 border-[#658a07]' : 'border-gray-200'}`}>
                  <p className="text-sm font-bold text-[#1f1f1f] truncate">{k.company_name || '—'}</p>
                  <p className="text-[11px] text-gray-500 truncate">{k.contact_name || '—'}</p>
                  {k.contact_email && <p className="text-[10px] text-gray-400 truncate">{k.contact_email}</p>}
                </li>
              ))}
            </ul>
          )}
        </aside>

        {/* RIGHT: Module-Cards-Grid (Drag-Target) */}
        <main className={`${mobileTab === 'module' ? 'flex' : 'hidden'} md:flex flex-col flex-1 overflow-y-auto bg-[#fafafa]`}>
          <div className="p-3 md:p-5">
            {cohortsSafe.length === 0 ? (
              <div className="border border-dashed border-gray-300 p-12 text-center text-gray-400">
                Noch keine Module. Klick "Neues Modul" oben rechts zum Anlegen.
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3">
                {cohortsSafe.map((c) => (
                  <CohortDropCard key={c.id} cohort={c}
                    isDragOver={dragOverCohortId === c.id}
                    isBusy={busyCohortId === c.id}
                    onOpen={() => setActiveCohortId(c.id)}
                    onDragOver={(e) => handleDragOver(e, c.id)}
                    onDragLeave={() => handleDragLeave(c.id)}
                    onDrop={(e) => handleDrop(e, c.id)}
                    onMemberDragStart={(e, rcId) => handleMemberDragStart(e, rcId, c.id)}
                    onMemberDragEnd={handleDragEnd}
                    onMemberClick={(member) => setActiveMember(member)} />
                ))}
              </div>
            )}
          </div>
        </main>
      </div>

      )}

      {showNew && <NewCohortModal onClose={() => setShowNew(false)} onCreated={(c) => { setShowNew(false); loadCohorts(); setActiveCohortId(c.id); }} />}
      {/* Mobile-Fallback: activeMember als overlay wenn kein Desktop-Side-Panel */}
      {activeMember && (
        <div className="md:hidden">
          <MemberKickoffModal
            leadId={activeMember.lead_id}
            companyName={activeMember.company_name}
            onClose={() => { setActiveMember(null); loadCohorts(); }} />
        </div>
      )}
    </div>
  );
}

function CohortDropCard({ cohort, isDragOver, isBusy, onOpen, onDragOver, onDragLeave, onDrop, onMemberDragStart, onMemberDragEnd, onMemberClick }) {
  const meta = COHORT_STATUS[cohort.status] || COHORT_STATUS.planning;
  const typeMeta = COHORT_TYPES[cohort.cohort_type] || COHORT_TYPES.modul_1;
  const members = cohort.members || [];

  return (
    <div
      onDragOver={onDragOver}
      onDragLeave={onDragLeave}
      onDrop={onDrop}
      className={`border bg-white transition-all ${
        isDragOver
          ? 'border-[#658a07] bg-[#befa0a]/15 shadow-lg'
          : 'border-gray-200'
      } ${isBusy ? 'opacity-60' : ''}`}>

      {/* Header — klickbar zum Öffnen des Detail-Modals */}
      <div onClick={onOpen} className="p-4 cursor-pointer hover:bg-gray-50/40 border-b border-gray-100">
        <div className="flex items-start justify-between gap-2 mb-1">
          <h3 className="font-bold text-[#1f1f1f]" style={{ fontFamily: "'Oswald', sans-serif" }}>{cohort.name}</h3>
          <span className={`text-[10px] uppercase tracking-wider px-2 py-0.5 rounded ${meta.cls}`}>{meta.label}</span>
        </div>
        <p className="text-[10px] uppercase tracking-wider text-[#658a07] mb-1 font-bold">{typeMeta.label}</p>
        <div className="flex items-center gap-3 text-[11px] text-gray-500 flex-wrap">
          <span className="inline-flex items-center gap-1"><Users size={11} /> {cohort.member_count} Kunden</span>
          {cohort.recurring_weekday !== null && cohort.recurring_weekday !== undefined && cohort.recurring_time && (
            <span className="inline-flex items-center gap-1"><Calendar size={11} /> {WEEKDAYS[cohort.recurring_weekday]} {cohort.recurring_time}</span>
          )}
        </div>
      </div>

      {/* Mitglieder-Liste (drag-source) */}
      <div className="p-3 space-y-1.5 min-h-[60px]">
        {members.length === 0 ? (
          <p className="text-[11px] text-gray-400 italic px-1">Noch keine Mitglieder.</p>
        ) : (
          members.map((m) => (
            <div key={m.ready_check_id}
              draggable
              onDragStart={(e) => onMemberDragStart && onMemberDragStart(e, m.ready_check_id)}
              onDragEnd={onMemberDragEnd}
              onClick={(e) => { e.stopPropagation(); if (onMemberClick) onMemberClick(m); }}
              title="Klick: 1:1-Termin / Details — Drag: in andere Kohorte"
              className="flex items-center justify-between gap-2 px-2.5 py-1.5 bg-gray-50 border border-gray-200 cursor-pointer hover:border-[#658a07] hover:bg-white">
              <span className="text-xs font-bold text-[#1f1f1f] truncate flex-1">{m.company_name || '—'}</span>
              <span className="text-[9px] uppercase tracking-wider text-gray-500 px-1.5 py-0.5 bg-white rounded flex-shrink-0">{m.status}</span>
            </div>
          ))
        )}
      </div>

      {/* Drop-Zone Footer */}
      <div className={`m-3 mt-1 border-2 border-dashed py-2.5 px-4 text-center text-xs uppercase tracking-wider ${
        isDragOver ? 'border-[#658a07] text-[#658a07] font-bold bg-[#befa0a]/10' : 'border-gray-200 text-gray-400'
      }`} style={{ fontFamily: "'Oswald', sans-serif" }}>
        {isBusy ? (
          <span className="inline-flex items-center gap-1"><Loader2 size={12} className="animate-spin" /> Wird zugeordnet…</span>
        ) : isDragOver ? (
          'Hier loslassen ↓'
        ) : (
          'Kunde hierher ziehen'
        )}
      </div>
    </div>
  );
}

// =============================================================================
// MemberKickoffModal — 1:1-Termin pro Kunde setzen
// =============================================================================

function MemberKickoffModal({ leadId, companyName, onClose, inline = false }) {
  const [status, setStatus] = useState(null);
  const [lead, setLead] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);
  const [date, setDate] = useState('');
  const [time, setTime] = useState('10:00');
  const [duration, setDuration] = useState(30);
  const [successMsg, setSuccessMsg] = useState('');
  const [leadEdits, setLeadEdits] = useState({});
  const [savingLead, setSavingLead] = useState(false);

  const load = useCallback(async () => {
    setLoading(true); setError('');
    try {
      // Status + Lead-Daten parallel laden
      const [{ data: statusData }, { data: leadData }] = await Promise.all([
        axios.get(`${API}/admin/leads/${leadId}/modul-1-status`, { headers: authHeaders() }),
        axios.get(`${API}/crm/leads/${leadId}`, { headers: authHeaders() }),
      ]);
      setStatus(statusData);
      setLead(leadData);
      setLeadEdits({
        company_name: leadData.company_name || '',
        contact_name: leadData.contact_name || '',
        contact_email: leadData.contact_email || '',
        contact_phone: leadData.contact_phone || '',
        industry: leadData.industry || '',
        num_locations: leadData.num_locations || 1,
        num_employees: leadData.num_employees || 0,
        pain_points: leadData.pain_points || '',
      });
      const kickoff = (statusData.kickoff_milestones || [])[0];
      if (kickoff?.scheduled_at) {
        const dt = new Date(kickoff.scheduled_at);
        setDate(dt.toISOString().slice(0, 10));
        setTime(dt.toTimeString().slice(0, 5));
      }
      if (kickoff?.duration_minutes) setDuration(kickoff.duration_minutes);
    } catch (e) {
      setError(e.response?.data?.detail || 'Konnte Kunde nicht laden');
    }
    setLoading(false);
  }, [leadId]);

  useEffect(() => { load(); }, [load]);

  const saveLead = async () => {
    setSavingLead(true); setError(''); setSuccessMsg('');
    try {
      await axios.patch(`${API}/crm/leads/${leadId}`, leadEdits, { headers: authHeaders() });
      setSuccessMsg('Kunden-Daten gespeichert.');
      await load();
    } catch (e) {
      setError(e.response?.data?.detail || 'Speichern fehlgeschlagen');
    }
    setSavingLead(false);
  };

  const [resendingWelcome, setResendingWelcome] = useState(false);
  const resendWelcome = async () => {
    const customerId = status?.customer_id;
    if (!customerId) { alert('Kein Portal-Kunden-Eintrag vorhanden'); return; }
    if (!window.confirm(`Welcome-Mail neu senden an ${leadEdits.contact_email}?\n\nDas alte Portal-Passwort wird ungültig — ein neues geht per Mail raus.`)) return;
    setResendingWelcome(true); setError(''); setSuccessMsg('');
    try {
      const { data } = await axios.post(`${API}/admin/customers/${customerId}/resend-welcome`, {}, { headers: authHeaders() });
      if (data.mail_sent) {
        setSuccessMsg(`Welcome-Mail neu versendet an ${leadEdits.contact_email}.`);
      } else {
        setError(`Mail-Versand fehlgeschlagen: ${data.mail_error || 'unbekannter Grund'}`);
      }
      await load();
    } catch (e) {
      setError(e.response?.data?.detail || 'Resend fehlgeschlagen');
    }
    setResendingWelcome(false);
  };

  const kickoff = (status?.kickoff_milestones || [])[0];

  const saveKickoff = async () => {
    if (!date || !time) { alert('Datum + Uhrzeit angeben'); return; }
    if (!kickoff) { alert('Kein Kickoff-Milestone gefunden'); return; }
    setSaving(true); setError(''); setSuccessMsg('');
    try {
      // ISO mit Berlin-Timezone — Server interpretiert das via fromisoformat
      const dt = `${date}T${time}:00`;
      const { data } = await axios.patch(`${API}/admin/milestones/${kickoff.id}`,
        { scheduled_at: dt, duration_minutes: parseInt(duration, 10) },
        { headers: authHeaders() });
      if (data.calendar_event_created) {
        setSuccessMsg(`Termin gespeichert. Calendar-Einladung + Meet-Link an Kunde verschickt.`);
      } else if (data.calendar_error) {
        setSuccessMsg(`Termin gespeichert. Calendar-Event fehlgeschlagen: ${data.calendar_error}`);
      } else {
        setSuccessMsg('Termin gespeichert.');
      }
      await load();
    } catch (e) {
      setError(e.response?.data?.detail || 'Speichern fehlgeschlagen');
    }
    setSaving(false);
  };

  const headerBlock = (
    <div className="bg-[#0a0a0a] text-white px-5 py-3 flex justify-between items-start sticky top-0 z-10">
      <div>
        <p className="text-[#befa0a] text-[10px] uppercase tracking-[0.3em] font-bold" style={{ fontFamily: "'Oswald', sans-serif" }}>
          Kunde · Modul 1
        </p>
        <h3 className="text-base font-bold uppercase" style={{ fontFamily: "'Oswald', sans-serif" }}>
          {leadEdits.company_name || companyName || 'Kunde'}
        </h3>
      </div>
      <button onClick={onClose} className="text-white/60 hover:text-white"><X size={18} /></button>
    </div>
  );

  const wrapInline = (content) => (
    <div className="bg-white h-full flex flex-col overflow-y-auto">{content}</div>
  );
  const wrapOverlay = (content) => (
    <div className="fixed inset-0 z-[100] bg-black/60 flex items-center justify-center p-4 overflow-y-auto" onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="bg-white w-full max-w-[560px] max-h-[calc(100vh-2rem)] overflow-y-auto">{content}</div>
    </div>
  );
  const wrap = inline ? wrapInline : wrapOverlay;

  return wrap(
    <>
        {headerBlock}

        {error && (
          <div className="bg-red-50 border-b border-red-200 text-red-700 p-3 text-sm">
            <AlertTriangle size={14} className="inline mr-2" /> {error}
          </div>
        )}
        {successMsg && (
          <div className="bg-emerald-50 border-b border-emerald-200 text-emerald-700 p-3 text-sm">
            <CheckCircle size={14} className="inline mr-2" /> {successMsg}
          </div>
        )}

        {loading ? (
          <div className="p-12 text-center"><Loader2 size={24} className="animate-spin mx-auto text-gray-400" /></div>
        ) : (
          <div className="p-5 space-y-6">
            {/* Kunden-Stammdaten (editierbar) */}
            <section>
              <h4 className="text-[10px] uppercase tracking-wider text-gray-500 font-bold mb-3" style={{ fontFamily: "'Oswald', sans-serif" }}>
                Kunden-Daten
              </h4>
              <div className="grid grid-cols-2 gap-2">
                <div className="col-span-2">
                  <label className="block text-[10px] uppercase tracking-wider text-gray-500 mb-1 font-bold">Firma</label>
                  <input type="text" value={leadEdits.company_name || ''} onChange={(e) => setLeadEdits((s) => ({ ...s, company_name: e.target.value }))}
                    className="w-full border border-gray-300 px-2 py-1.5 text-sm focus:outline-none focus:border-[#befa0a]" />
                </div>
                <div>
                  <label className="block text-[10px] uppercase tracking-wider text-gray-500 mb-1 font-bold">Kontakt-Name</label>
                  <input type="text" value={leadEdits.contact_name || ''} onChange={(e) => setLeadEdits((s) => ({ ...s, contact_name: e.target.value }))}
                    className="w-full border border-gray-300 px-2 py-1.5 text-sm focus:outline-none focus:border-[#befa0a]" />
                </div>
                <div>
                  <label className="block text-[10px] uppercase tracking-wider text-gray-500 mb-1 font-bold">Telefon</label>
                  <input type="text" value={leadEdits.contact_phone || ''} onChange={(e) => setLeadEdits((s) => ({ ...s, contact_phone: e.target.value }))}
                    className="w-full border border-gray-300 px-2 py-1.5 text-sm focus:outline-none focus:border-[#befa0a]" />
                </div>
                <div className="col-span-2">
                  <label className="block text-[10px] uppercase tracking-wider text-gray-500 mb-1 font-bold">E-Mail (= Portal-Login)</label>
                  <input type="email" value={leadEdits.contact_email || ''} onChange={(e) => setLeadEdits((s) => ({ ...s, contact_email: e.target.value }))}
                    className="w-full border border-gray-300 px-2 py-1.5 text-sm focus:outline-none focus:border-[#befa0a]" />
                  {status?.customer_email && status.customer_email.toLowerCase() !== (leadEdits.contact_email || '').toLowerCase() ? (
                    <div className="mt-1 bg-amber-50 border border-amber-300 text-amber-800 text-[11px] p-2 rounded space-y-2">
                      <p><strong>⚠ Sync nicht synchron:</strong> Im Kunden-Portal/Calendar steht <code className="bg-white px-1 rounded">{status.customer_email}</code>.</p>
                      <div className="flex gap-2 flex-wrap">
                        <button onClick={async () => {
                          try {
                            const { data } = await axios.post(`${API}/admin/leads/${leadId}/force-email-sync`, {}, { headers: authHeaders() });
                            if (data.ok) {
                              setSuccessMsg(`Force-Sync OK: ${data.updated.length} Customer aktualisiert.`);
                              await load();
                            } else {
                              setError(`Force-Sync fehlgeschlagen: ${data.reason || 'unbekannt'}`);
                            }
                          } catch (e) { setError(e.response?.data?.detail || 'Force-Sync Fehler'); }
                        }}
                          className="bg-amber-700 text-white px-3 py-1 text-[10px] uppercase tracking-wider font-bold hover:bg-amber-800"
                          style={{ fontFamily: "'Oswald', sans-serif" }}>
                          🔨 Force-Sync (überschreibt Customer)
                        </button>
                        <button onClick={async () => {
                          try {
                            const { data } = await axios.get(`${API}/admin/leads/${leadId}/email-debug`, { headers: authHeaders() });
                            window.alert(JSON.stringify(data, null, 2));
                          } catch (e) { window.alert(e.response?.data?.detail || 'Debug-Call fehlgeschlagen'); }
                        }}
                          className="bg-white border border-amber-400 text-amber-700 px-3 py-1 text-[10px] uppercase tracking-wider font-bold hover:bg-amber-100"
                          style={{ fontFamily: "'Oswald', sans-serif" }}>
                          🔍 Debug-Info zeigen
                        </button>
                      </div>
                    </div>
                  ) : (
                    <p className="text-[10px] text-gray-400 mt-1">Wird beim Speichern auch fürs Kunden-Portal aktualisiert. Bei Änderung danach "Welcome-Mail neu senden" für frisches Passwort.</p>
                  )}
                </div>
                <div>
                  <label className="block text-[10px] uppercase tracking-wider text-gray-500 mb-1 font-bold">Branche</label>
                  <input type="text" value={leadEdits.industry || ''} onChange={(e) => setLeadEdits((s) => ({ ...s, industry: e.target.value }))}
                    className="w-full border border-gray-300 px-2 py-1.5 text-sm focus:outline-none focus:border-[#befa0a]" />
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <label className="block text-[10px] uppercase tracking-wider text-gray-500 mb-1 font-bold">Standorte</label>
                    <input type="number" min={1} value={leadEdits.num_locations || 1} onChange={(e) => setLeadEdits((s) => ({ ...s, num_locations: parseInt(e.target.value, 10) || 1 }))}
                      className="w-full border border-gray-300 px-2 py-1.5 text-sm focus:outline-none focus:border-[#befa0a]" />
                  </div>
                  <div>
                    <label className="block text-[10px] uppercase tracking-wider text-gray-500 mb-1 font-bold">MA</label>
                    <input type="number" min={0} value={leadEdits.num_employees || 0} onChange={(e) => setLeadEdits((s) => ({ ...s, num_employees: parseInt(e.target.value, 10) || 0 }))}
                      className="w-full border border-gray-300 px-2 py-1.5 text-sm focus:outline-none focus:border-[#befa0a]" />
                  </div>
                </div>
                <div className="col-span-2">
                  <label className="block text-[10px] uppercase tracking-wider text-gray-500 mb-1 font-bold">Pain Points / Notiz</label>
                  <textarea rows={2} value={leadEdits.pain_points || ''} onChange={(e) => setLeadEdits((s) => ({ ...s, pain_points: e.target.value }))}
                    className="w-full border border-gray-300 px-2 py-1.5 text-sm focus:outline-none focus:border-[#befa0a] resize-y" />
                </div>
              </div>
              <div className="flex justify-between items-center mt-2 gap-2 flex-wrap">
                {status?.customer_id ? (
                  <button onClick={resendWelcome} disabled={resendingWelcome}
                    className="border border-[#658a07] text-[#658a07] px-3 py-1.5 text-[10px] uppercase tracking-wider font-bold hover:bg-[#658a07] hover:text-white disabled:opacity-50"
                    style={{ fontFamily: "'Oswald', sans-serif" }}
                    title="Sendet eine neue Welcome-Mail mit frischem Passwort an die aktuelle E-Mail.">
                    {resendingWelcome ? 'Sende…' : '✉ Welcome-Mail neu senden'}
                  </button>
                ) : <span />}
                <button onClick={saveLead} disabled={savingLead}
                  className="bg-gray-900 text-white px-3 py-1.5 text-[10px] uppercase tracking-wider font-bold hover:bg-black disabled:opacity-50"
                  style={{ fontFamily: "'Oswald', sans-serif" }}>
                  {savingLead ? 'Speichere…' : 'Kunden-Daten speichern'}
                </button>
              </div>
            </section>

            {/* 1:1-Kickoff-Termin */}
            <section className="border-t border-gray-200 pt-5">
              <h4 className="text-[10px] uppercase tracking-wider text-gray-500 font-bold mb-2" style={{ fontFamily: "'Oswald', sans-serif" }}>
                1:1-Kickoff-Termin
              </h4>
              {!kickoff ? (
                <p className="text-xs text-gray-500 italic">
                  Kein Kickoff-Milestone gefunden. (Migration-Restbestand. Neue Kunden bekommen automatisch einen.)
                </p>
              ) : (
              <>
            <p className="text-xs text-gray-600">
              30-Minuten-Kickoff. Beim Speichern wird ein Google-Calendar-Event mit Meet-Link an den Kunden geschickt, plus 24h-Erinnerung.
            </p>

            <div className="grid grid-cols-3 gap-2">
              <div>
                <label className="block text-[10px] uppercase tracking-wider text-gray-500 mb-1 font-bold">Datum</label>
                <input type="date" value={date} onChange={(e) => setDate(e.target.value)}
                  className="w-full border border-gray-300 px-2 py-2 text-sm focus:outline-none focus:border-[#befa0a]" />
              </div>
              <div>
                <label className="block text-[10px] uppercase tracking-wider text-gray-500 mb-1 font-bold">Uhrzeit</label>
                <input type="time" value={time} onChange={(e) => setTime(e.target.value)}
                  className="w-full border border-gray-300 px-2 py-2 text-sm focus:outline-none focus:border-[#befa0a]" />
              </div>
              <div>
                <label className="block text-[10px] uppercase tracking-wider text-gray-500 mb-1 font-bold">Dauer (min)</label>
                <input type="number" value={duration} onChange={(e) => setDuration(e.target.value)} min={15} max={120}
                  className="w-full border border-gray-300 px-2 py-2 text-sm focus:outline-none focus:border-[#befa0a]" />
              </div>
            </div>

            {kickoff.scheduled_at && (
              <div className="bg-gray-50 border border-gray-200 p-3 text-xs space-y-1">
                <p className="text-[10px] uppercase tracking-wider text-gray-500 font-bold">Aktuell geplant</p>
                <p className="text-gray-700">
                  {new Date(kickoff.scheduled_at).toLocaleString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' })} Uhr
                  {kickoff.duration_minutes ? ` (${kickoff.duration_minutes} min)` : ''}
                </p>
                {kickoff.meeting_url && (
                  <p>
                    <a href={kickoff.meeting_url} target="_blank" rel="noreferrer" className="text-[#658a07] hover:underline inline-flex items-center gap-1">
                      Google Meet öffnen <ExternalLink size={11} />
                    </a>
                  </p>
                )}
                {kickoff.done_at && <p className="text-emerald-700 font-bold">✓ Erledigt: {new Date(kickoff.done_at).toLocaleDateString('de-DE')}</p>}
              </div>
            )}

            <div className="flex justify-end gap-2 pt-1">
              <button onClick={saveKickoff} disabled={saving || !date || !time}
                className="bg-[#0a0a0a] text-[#befa0a] px-4 py-2 text-xs font-bold uppercase tracking-wider hover:bg-[#befa0a] hover:text-[#0a0a0a] disabled:opacity-50 inline-flex items-center gap-2"
                style={{ fontFamily: "'Oswald', sans-serif" }}>
                {saving ? <><Loader2 size={12} className="animate-spin" /> Speichere…</> : <><Calendar size={14} /> Termin speichern + senden</>}
              </button>
            </div>
              </>
              )}
            </section>

            {/* Mail-Versand-Historie — zeigt was an den Kunden raus ging */}
            <section className="border-t border-gray-200 pt-5">
              <h4 className="text-[10px] uppercase tracking-wider text-gray-500 font-bold mb-2" style={{ fontFamily: "'Oswald', sans-serif" }}>
                Versendete Mails ({(lead?.interactions || []).filter((i) => i.type === 'email' || i.type === 'email_failed').length})
              </h4>
              <MailHistoryList
                interactions={lead?.interactions || []}
                customerId={status?.customer_id}
                kickoffId={kickoff?.id}
                currentEmail={leadEdits.contact_email}
                onResend={async (kind) => {
                  setError(''); setSuccessMsg('');
                  try {
                    if (kind === 'welcome') {
                      if (!status?.customer_id) throw new Error('Kein Kunden-Eintrag');
                      const { data } = await axios.post(`${API}/admin/customers/${status.customer_id}/resend-welcome`, {}, { headers: authHeaders() });
                      setSuccessMsg(data.mail_sent ? `Welcome neu gesendet an ${leadEdits.contact_email}.` : `Fehler: ${data.mail_error || 'unbekannt'}`);
                    } else if (kind === 'kickoff_invite') {
                      if (!kickoff?.id || !kickoff?.scheduled_at) throw new Error('Kein gesetzter Kickoff-Termin');
                      // Re-trigger über patch_milestone mit bestehenden Werten → erzeugt Update-Mail
                      const dt = `${date}T${time}:00`;
                      const { data } = await axios.patch(`${API}/admin/milestones/${kickoff.id}`,
                        { scheduled_at: dt, duration_minutes: parseInt(duration, 10) },
                        { headers: authHeaders() });
                      if (data.calendar_event_created) {
                        setSuccessMsg(`Kickoff-Einladung neu gesendet an ${leadEdits.contact_email}.`);
                      } else {
                        setError(`Kickoff-Mail Fehler: ${data.calendar_error || 'unbekannt'}`);
                      }
                    }
                    await load();
                  } catch (e) {
                    setError(e.response?.data?.detail || e.message || 'Resend fehlgeschlagen');
                  }
                }}
              />
            </section>
          </div>
        )}
    </>);
}

function MailHistoryList({ interactions, customerId, kickoffId, currentEmail, onResend }) {
  const MAIL_KIND_LABEL = {
    welcome: 'Welcome-Mail (Portal-Zugang)',
    kickoff_invite: 'Kickoff-Einladung',
    employee_invite: 'Mitarbeiter-Einladung',
    employee_reinvite: 'Mitarbeiter Re-Invite',
    cohort_invite: 'Gruppen-Termin Einladung',
  };
  const RESENDABLE = {
    welcome: !!customerId,
    kickoff_invite: !!kickoffId,
  };
  const [busyKind, setBusyKind] = useState(null);

  const mails = (interactions || [])
    .filter((i) => i.type === 'email' || i.type === 'email_failed')
    .sort((a, b) => new Date(b.created_at || 0) - new Date(a.created_at || 0));

  // Wenn ein Customer existiert aber KEIN echter Welcome-Mail-Log da ist
  // (z.B. weil die Mail vor dem Logging-Patch versendet wurde), injiziere
  // einen synthetischen Eintrag damit Sebastian den Resend-Button hat.
  const hasWelcomeLog = mails.some((m) => m.email_subtype === 'welcome');
  if (customerId && !hasWelcomeLog) {
    mails.unshift({
      _synthetic: true,
      id: 'synth-welcome',
      type: 'email',
      email_subtype: 'welcome',
      subject: 'Welcome-Mail (Portal-Zugang) — historisch, nicht im Log',
      content: 'Status unbekannt. Initial-Versand erfolgte vor dem Mail-Logging. Über "↻ Neu senden" eine frische Welcome-Mail mit neuem Passwort an die aktuelle Adresse triggern.',
      created_at: null,
    });
  }

  if (mails.length === 0) {
    return (
      <p className="text-xs text-gray-400 italic">
        Noch keine Mails versendet. Sobald eine Welcome-Mail, Kickoff-Einladung oder Mitarbeiter-Invite raus geht, taucht sie hier auf.
      </p>
    );
  }
  const handleResend = async (kind) => {
    if (!RESENDABLE[kind]) return;
    if (!window.confirm(`Mail (${MAIL_KIND_LABEL[kind] || kind}) neu senden an ${currentEmail || '(aktuelle Adresse)'}?`)) return;
    setBusyKind(kind);
    try {
      await onResend(kind);
    } finally {
      setBusyKind(null);
    }
  };
  return (
    <ul className="space-y-1.5">
      {mails.map((m, i) => {
        const failed = m.type === 'email_failed';
        const dt = m.created_at ? new Date(m.created_at) : null;
        const kind = m.email_subtype;
        const label = MAIL_KIND_LABEL[kind] || kind || 'Mail';
        const canResend = !!RESENDABLE[kind];
        return (
          <li key={m.id || i}
            className={`text-xs p-2 border ${failed ? 'border-red-200 bg-red-50/40' : m._synthetic ? 'border-dashed border-gray-300 bg-gray-50/30' : 'border-gray-200 bg-white'}`}>
            <div className="flex items-center justify-between gap-2 mb-0.5">
              <span className="font-bold text-[#1f1f1f]">
                {failed ? '⚠️ Fehlgeschlagen' : m._synthetic ? '○' : '✓'} {label}
              </span>
              <div className="flex items-center gap-2 flex-shrink-0">
                {canResend && (
                  <button
                    onClick={() => handleResend(kind)}
                    disabled={busyKind === kind}
                    className="text-[10px] uppercase tracking-wider border border-[#658a07] text-[#658a07] px-2 py-0.5 hover:bg-[#658a07] hover:text-white disabled:opacity-50"
                    style={{ fontFamily: "'Oswald', sans-serif" }}
                    title={`Neu senden an ${currentEmail || 'aktuelle Adresse'}`}>
                    {busyKind === kind ? '…' : '↻ Neu senden'}
                  </button>
                )}
                {dt && (
                  <span className="text-[10px] text-gray-400">
                    {dt.toLocaleDateString('de-DE')} {dt.toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' })}
                  </span>
                )}
              </div>
            </div>
            {m.subject && <p className="text-[11px] text-gray-600 truncate">{m.subject}</p>}
            {m.content && <p className="text-[11px] text-gray-500 whitespace-pre-line">{m.content}</p>}
          </li>
        );
      })}
    </ul>
  );
}
